import React, { useState, FormEvent } from 'react';

// Definim interfața pentru props
interface ProposalDetailProps {
  onBack: () => void;
}

// Definim interfața pentru propunere
interface Proposal {
  id: number;
  titlu: string;
  descriere: string;
  voturi: number;
  vizualizari: number;
  comentarii: number;
  utilizator: string;
  timestamp: string;
  taguri: string[];
  status?: string;
  etapePlan?: string[];
  beneficii?: string[];
}

// Definim interfața pentru comentarii
interface Comment {
  id: number;
  text: string;
  utilizator: string;
  timestamp: string;
  voturi: number;
  tip?: 'sugestie' | 'întrebare' | 'susținere';
}

// Tipul pentru direcția de vot
type VoteDirection = 'up' | 'down';

const ProposalDetail: React.FC<ProposalDetailProps> = ({ onBack }) => {
  // Date exemplu pentru propunere
  const [proposal, setProposal] = useState<Proposal>({
    id: 1,
    titlu: "Platformă digitală pentru colaborare între antreprenori locali",
    descriere: "Propun crearea unei platforme online dedicate conectării antreprenorilor din regiunea noastră pentru a facilita colaborări, parteneriate și schimb de resurse.\n\nPlatforma ar putea include:\n- Profil pentru fiecare afacere cu descrierea activității și nevoilor\n- Sistem de mesagerie pentru comunicare directă\n- Secțiune de anunțuri pentru resurse disponibile sau necesare\n- Calendar cu evenimente de networking și oportunități de colaborare\n- Forum de discuții pe diverse teme relevante\n\nImplementarea unei astfel de platforme ar putea crește semnificativ colaborarea dintre antreprenorii locali și ar facilita parteneriate care altfel ar fi dificil de stabilit.",
    voturi: 15,
    vizualizari: 124,
    comentarii: 3,
    utilizator: "Maria Popescu",
    timestamp: "acum 2 ore",
    taguri: ["digitalizare", "colaborare", "networking"],
    status: "În analiză",
    etapePlan: [
      "Definirea funcționalităților exacte și consultarea cu potențialii utilizatori",
      "Dezvoltarea unei versiuni beta și testare cu un grup restrâns",
      "Lansarea platformei și promovarea în rândul membrilor patronatului",
      "Colectarea feedback-ului și implementarea îmbunătățirilor"
    ],
    beneficii: [
      "Creșterea vizibilității afacerilor locale",
      "Facilitarea parteneriatelor și colaborărilor",
      "Crearea unei comunități antreprenoriale mai unite",
      "Eficientizarea comunicării între membrii patronatului",
      "Acces mai ușor la resurse și oportunități locale"
    ]
  });

  // Date exemplu pentru comentarii
  const [comentarii, setComentarii] = useState<Comment[]>([
    {
      id: 1,
      text: "Propunere excelentă! Aș adăuga și o secțiune pentru partajarea de resurse educaționale și best practices între antreprenori. De asemenea, cred că ar fi util un sistem de rating pentru a asigura calitatea colaborărilor.",
      utilizator: "Alexandru Ionescu",
      timestamp: "acum 1 oră",
      voturi: 8,
      tip: "sugestie"
    },
    {
      id: 2,
      text: "Care ar fi costurile estimate pentru dezvoltarea și întreținerea unei astfel de platforme? Ar trebui să discutăm și despre un model de finanțare pe termen lung pentru a asigura sustenabilitatea proiectului.",
      utilizator: "Elena Dumitru",
      timestamp: "acum 45 minute",
      voturi: 5,
      tip: "întrebare"
    },
    {
      id: 3,
      text: "Susțin complet această inițiativă! Afacerea mea ar beneficia enorm de pe urma unei astfel de platforme. Sunt dispus să contribui și cu resurse pentru implementare dacă este nevoie.",
      utilizator: "Dan Popovici",
      timestamp: "acum 20 minute",
      voturi: 3,
      tip: "susținere"
    }
  ]);

  // State pentru noul comentariu
  const [newComment, setNewComment] = useState<string>("");
  const [commentType, setCommentType] = useState<'sugestie' | 'întrebare' | 'susținere'>('sugestie');

  // Funcție pentru votare propunere
  const handleProposalVote = (direction: VoteDirection): void => {
    setProposal(prev => ({
      ...prev,
      voturi: direction === 'up' ? prev.voturi + 1 : prev.voturi - 1
    }));
  };

  // Funcție pentru votare comentariu
  const handleCommentVote = (id: number, direction: VoteDirection): void => {
    setComentarii(prev => 
      prev.map(com => 
        com.id === id 
          ? {...com, voturi: direction === 'up' ? com.voturi + 1 : com.voturi - 1} 
          : com
      )
    );
  };

  // Funcție pentru adăugare comentariu nou
  const handleAddComment = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (newComment.trim() === "") return;
    
    const newCommentObj: Comment = {
      id: comentarii.length + 1,
      text: newComment,
      utilizator: "Utilizator Curent",
      timestamp: "acum câteva secunde",
      voturi: 0,
      tip: commentType
    };
    
    setComentarii(prev => [...prev, newCommentObj]);
    setProposal(prev => ({...prev, comentarii: prev.comentarii + 1}));
    setNewComment("");
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header cu navigare înapoi */}
      <div className="p-4 border-b flex items-center">
        <button 
          onClick={onBack}
          className="mr-3 text-gray-600 hover:text-blue-600"
        >
          ← Înapoi
        </button>
        <h1 className="text-xl font-semibold">Detalii Propunere</h1>
      </div>

      {/* Conținutul propunerii */}
      <div className="p-4 border-b">
        <div className="flex">
          {/* Votare */}
          <div className="flex flex-col items-center mr-6">
            <button 
              className="p-2 text-gray-600 hover:text-blue-600"
              onClick={() => handleProposalVote('up')}
            >
              <span className="text-2xl">▲</span>
            </button>
            <span className="font-semibold text-lg">{proposal.voturi}</span>
            <button 
              className="p-2 text-gray-600 hover:text-blue-600"
              onClick={() => handleProposalVote('down')}
            >
              <span className="text-2xl">▼</span>
            </button>
          </div>

          {/* Conținut */}
          <div className="flex-1">
            <div className="flex justify-between items-start mb-2">
              <h2 className="text-2xl font-semibold text-gray-800">
                {proposal.titlu}
              </h2>
              {proposal.status && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  {proposal.status}
                </span>
              )}
            </div>
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <span className="mr-4">
                <span className="mr-1">👁️</span> {proposal.vizualizari} vizualizări
              </span>
              <span className="mr-4">
                <span className="mr-1">💬</span> {proposal.comentarii} comentarii
              </span>
              <span>
                <span className="mr-1">⏱️</span> {proposal.timestamp}
              </span>
            </div>
            <div className="text-gray-700 mb-4 whitespace-pre-line">
              {proposal.descriere}
            </div>
            
            {/* Plan de implementare */}
            {proposal.etapePlan && proposal.etapePlan.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-lg mb-2">Plan de implementare:</h3>
                <ol className="list-decimal pl-5 space-y-1">
                  {proposal.etapePlan.map((etapa, index) => (
                    <li key={index} className="text-gray-700">{etapa}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* Beneficii */}
            {proposal.beneficii && proposal.beneficii.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-lg mb-2">Beneficii:</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {proposal.beneficii.map((beneficiu, index) => (
                    <li key={index} className="text-gray-700">{beneficiu}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-3">
              {proposal.taguri.map((tag, index) => (
                <a key={index} href="#" className="px-2 py-1 bg-gray-100 text-xs rounded-md hover:bg-gray-200">
                  {tag}
                </a>
              ))}
            </div>
            <div className="flex items-center justify-end text-sm text-gray-500 mt-4">
              <div className="flex items-center">
                <span className="text-sm mr-2">👤</span>
                <span>{proposal.utilizator}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comentarii */}
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold mb-4">{proposal.comentarii} Comentarii</h3>
        
        {comentarii.map((comentariu) => (
          <div key={comentariu.id} className="border-b last:border-b-0 py-4">
            <div className="flex">
              {/* Votare comentariu */}
              <div className="flex flex-col items-center mr-6">
                <button 
                  className="p-1 text-gray-600 hover:text-blue-600"
                  onClick={() => handleCommentVote(comentariu.id, 'up')}
                >
                  <span className="text-xl">▲</span>
                </button>
                <span className="font-semibold">{comentariu.voturi}</span>
                <button 
                  className="p-1 text-gray-600 hover:text-blue-600"
                  onClick={() => handleCommentVote(comentariu.id, 'down')}
                >
                  <span className="text-xl">▼</span>
                </button>
              </div>

              {/* Conținut comentariu */}
              <div className="flex-1">
                {comentariu.tip && (
                  <div className="mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      comentariu.tip === 'sugestie' 
                        ? 'bg-green-100 text-green-800' 
                        : comentariu.tip === 'întrebare' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-blue-100 text-blue-800'
                    }`}>
                      {comentariu.tip === 'sugestie' 
                        ? '💡 Sugestie' 
                        : comentariu.tip === 'întrebare' 
                          ? '❓ Întrebare' 
                          : '👍 Susținere'}
                    </span>
                  </div>
                )}
                <div className="text-gray-700 mb-3">
                  {comentariu.text}
                </div>
                <div className="flex items-center justify-end text-sm text-gray-500">
                  <div className="flex items-center">
                    <span className="text-sm mr-2">👤</span>
                    <span>{comentariu.utilizator}</span>
                  </div>
                  <div className="ml-4 flex items-center">
                    <span className="text-sm mr-1">⏱️</span>
                    <span>{comentariu.timestamp}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Formular pentru comentariu nou */}
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-4">Adaugă un comentariu</h3>
        <form onSubmit={handleAddComment}>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip comentariu:</label>
            <div className="flex gap-3">
              <label className="inline-flex items-center">
                <input 
                  type="radio" 
                  className="form-radio text-blue-600" 
                  name="comment-type" 
                  value="sugestie" 
                  checked={commentType === 'sugestie'}
                  onChange={() => setCommentType('sugestie')}
                />
                <span className="ml-2">💡 Sugestie</span>
              </label>
              <label className="inline-flex items-center">
                <input 
                  type="radio" 
                  className="form-radio text-blue-600" 
                  name="comment-type" 
                  value="întrebare"
                  checked={commentType === 'întrebare'}
                  onChange={() => setCommentType('întrebare')}
                />
                <span className="ml-2">❓ Întrebare</span>
              </label>
              <label className="inline-flex items-center">
                <input 
                  type="radio" 
                  className="form-radio text-blue-600" 
                  name="comment-type" 
                  value="susținere"
                  checked={commentType === 'susținere'}
                  onChange={() => setCommentType('susținere')}
                />
                <span className="ml-2">👍 Susținere</span>
              </label>
            </div>
          </div>
          <textarea
            className="w-full border rounded-md p-3 min-h-32 mb-4"
            placeholder="Scrie comentariul tău aici..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            required
          />
          <button 
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
          >
            Publică comentariul
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProposalDetail;